// Per-unit meters panel. Mounted under each unit card on the property
// wizard (Units step) and the property sheet. Lists active meters
// (water/electricity/gas/heat/fee/other) with their pricing, plus a
// foldable "Add meter" form. Pricing is stored in minor units;
// monthly_fee + unit_price * delta is what billing later uses.

import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { icon } from '../core/icons.js';
import { t } from '../core/i18n.js';

const KINDS = ['water', 'electricity', 'gas', 'heat', 'fee', 'other'];

// Default unit-of-measure suggestion per kind. The user can still type
// anything they like; the backend only validates that the string is short.
const DEFAULT_UOM = {
  water: 'm³',
  electricity: 'kWh',
  gas: 'm³',
  heat: 'GJ',
  fee: '-',
  other: '',
};

const KIND_ICON = {
  water: 'droplet',
  electricity: 'zap',
  gas: 'flame',
  heat: 'thermometer',
  fee: 'tag',
  other: 'gauge',
};

const KIND_TINT = {
  water: 'primary',
  electricity: 'warning',
  gas: 'danger',
  heat: 'accent',
  fee: 'muted',
  other: 'muted',
};

export class UnitMetersPanel extends Component {
  constructor({ unitId, canManage }) {
    super();
    this.unitId = unitId;
    this.canManage = !!canManage;
    this.state = {
      meters: null,         // null = loading, [] = loaded empty
      error: null,
      busy: false,
      addOpen: false,
      panelOpen: true,      // <details open> state; user can collapse, persists across re-renders
      newMeter: this._emptyMeter(),
      editingMeterId: null, // when set, the form renders the edit-draft and PUTs on save
      editDraft: null,
    };
  }

  _emptyMeter() {
    const today = new Date().toISOString().slice(0, 10);
    return {
      kind: 'water',
      label: '',
      serial_number: '',
      unit_of_measure: DEFAULT_UOM.water,
      monthly_fee: '',
      unit_price: '',
      tier_quota_annual: '',
      tier_price_above: '',
      reading_day_of_month: '',
      starting_value: '',
      starting_date: today,
      starting_notes: '',
      notes: '',
    };
  }

  _draftFromRow(r) {
    return {
      id: r.id,
      kind: r.kind || 'water',
      label: r.label || '',
      serial_number: r.serial_number || '',
      unit_of_measure: r.unit_of_measure || '',
      monthly_fee: r.monthly_fee != null ? String(Number(r.monthly_fee) / 100) : '',
      unit_price: r.unit_price != null ? String(Number(r.unit_price) / 100) : '',
      tier_quota_annual: r.tier_quota_annual != null ? String(Number(r.tier_quota_annual)) : '',
      tier_price_above: r.tier_price_above != null ? String(Number(r.tier_price_above) / 100) : '',
      reading_day_of_month: r.reading_day_of_month != null ? String(r.reading_day_of_month) : '',
      notes: r.notes || '',
    };
  }

  async _load() {
    try {
      const r = await api.get(`/units/${this.unitId}/meters`);
      this.setState({ meters: Array.isArray(r.data) ? r.data : (Array.isArray(r) ? r : []), error: null });
    } catch (e) {
      const msg = e instanceof ApiError ? (e.body && e.body.error) || e.message : 'Could not load meters';
      this.setState({ meters: [], error: msg });
    }
  }

  template() {
    const m = this.state.meters;
    const count = Array.isArray(m) ? m.length : 0;
    const title = `${t('unit.meters.title') || 'Meters'} · ${count}`;
    const meta = String(t('unit.meters.attached_label') || 'Attached to this unit').toUpperCase();
    const addLabel = `+ ${String(t('unit.meters.add') || 'Add meter').toUpperCase()}`;
    const open = this.state.panelOpen !== false;
    return `
      <details class="unit-section-fold" data-unit-meters-panel${open ? ' open' : ''}>
        <summary>
          <span>${esc(title)}</span>
          <span class="unit-section-fold-meta">${esc(meta)}</span>
        </summary>
        <div class="unit-section-fold-body">
          ${this.state.error ? `<div class="error-banner">${esc(this.state.error)}</div>` : ''}
          ${m === null
            ? `<div class="muted">${esc(t('unit.meters.loading') || 'Loading…')}</div>`
            : count === 0
              ? `<div class="empty" style="background: var(--color-bg-card); border: 1px dashed var(--color-border); border-radius: 8px; padding: 16px; text-align: center; color: var(--color-text-faint);">${esc(t('unit.meters.empty') || 'No meters yet — add one to start tracking consumption.')}</div>`
              : `<div class="stack-2">${m.map((r) => this._rowHtml(r)).join('')}</div>`}
          ${this.canManage
            ? (this.state.addOpen
                ? this._addFormHtml()
                : `<button type="button" class="add-meter-fold" data-action="open-add"><span class="add-meter-fold-summary">${esc(addLabel)}</span></button>`)
            : ''}
        </div>
      </details>
    `;
  }

  _rowHtml(r) {
    const kind = r.kind || 'other';
    const tint = KIND_TINT[kind] || 'muted';
    const ic = KIND_ICON[kind] || 'gauge';
    const label = r.label || (t('unit.meters.kind.' + kind) || kind);
    const sn = r.serial_number ? `· ${esc(r.serial_number)}` : '';
    const uom = r.unit_of_measure || '';
    const fee = r.monthly_fee != null ? `${(Number(r.monthly_fee) / 100).toFixed(2)}/mo` : '';
    const price = r.unit_price != null ? `${(Number(r.unit_price) / 100).toFixed(2)}/${uom || 'unit'}` : '';
    const tier = r.tier_quota_annual != null && r.tier_price_above != null
      ? `> ${Number(r.tier_quota_annual)} ${uom || 'unit'}: ${(Number(r.tier_price_above) / 100).toFixed(2)}/${uom || 'unit'}`
      : '';
    const pricing = [fee, price, tier].filter(Boolean).join(' + ');
    return `
      <div class="meter-card kind-${esc(r.kind || 'other')}" data-meter-id="${esc(r.id)}">
        <div class="meter-card-ic">${icon(ic, { size: 'sm' })}</div>
        <div class="meter-card-main">
          <div class="meter-card-nm">${esc(label)}</div>
          <div class="meter-card-mt">${sn}${pricing ? ' \u00b7 ' + esc(pricing) : ''}</div>
        </div>
        <div class="meter-card-rd"></div>
        ${this.canManage
          ? `<div class="row" style="gap:6px; align-items:center;">
              <button type="button" class="btn-mono" data-action="edit-meter" data-id="${esc(r.id)}">${esc(t('common.edit') || 'Edit')}</button>
              <button type="button" class="btn btn-ghost btn-icon" data-action="remove-meter" data-id="${esc(r.id)}" aria-label="${esc(t('common.remove'))}">${icon('trash', { size: 'sm' })}</button>
            </div>`
          : ''}
      </div>
    `;
  }

  _addFormHtml() {
    const editing = this.state.editingMeterId != null;
    const m = editing ? this.state.editDraft : this.state.newMeter;
    const heading = editing
      ? String(t('unit.meters.edit_heading') || 'Edit meter').toUpperCase()
      : `+ ${String(t('unit.meters.add') || 'New meter').toUpperCase()}`;
    const opt = esc(t('common.optional'));
    const opthint = (label) => `<span class="faint" style="text-transform:none; letter-spacing:0; font-size:12px;">(${opt})</span>`;
    return `
      <form class="add-meter-fold open" data-add-form autocomplete="off">
        <div class="add-meter-fold-summary">${esc(heading)}</div>
        <div data-form-error></div>
        <div class="row" style="gap:var(--space-3); flex-wrap:wrap;">
          <div class="field grow"><label>${esc(t('unit.meters.kind') || 'Kind')}</label>
            <select class="select" name="kind">
              ${KINDS.map((k) => `<option value="${k}" ${m.kind === k ? 'selected' : ''}>${esc(t('unit.meters.kind.' + k) || k)}</option>`).join('')}
            </select></div>
          <div class="field" style="max-width:140px"><label>${esc(t('unit.meters.uom') || 'Unit')}</label>
            <input class="input" name="unit_of_measure" value="${esc(m.unit_of_measure)}" placeholder="m³ / kWh / GJ" /></div>
        </div>
        <div class="row" style="gap:var(--space-3); flex-wrap:wrap;">
          <div class="field grow"><label>${esc(t('unit.meters.label') || 'Label')} ${opthint()}</label>
            <input class="input" name="label" value="${esc(m.label)}" placeholder="${esc(t('unit.meters.label_placeholder') || 'e.g. Cold water')}" /></div>
          <div class="field grow"><label>${esc(t('unit.meters.serial') || 'Serial №')} ${opthint()}</label>
            <input class="input" name="serial_number" value="${esc(m.serial_number)}" placeholder="e.g. W-1234" /></div>
        </div>
        <div class="row" style="gap:var(--space-3); flex-wrap:wrap;">
          <div class="field grow"><label>${esc(t('unit.meters.monthly_fee') || 'Monthly fee')} ${opthint()}</label>
            <input class="input" name="monthly_fee" type="number" step="0.01" min="0" value="${esc(m.monthly_fee)}" placeholder="e.g. 12500" /></div>
          <div class="field grow"><label>${esc(t('unit.meters.unit_price') || 'Unit price')} ${opthint()}</label>
            <input class="input" name="unit_price" type="number" step="0.0001" min="0" value="${esc(m.unit_price)}" placeholder="e.g. 65" /></div>
        </div>
        ${m.kind === 'fee' ? '' : `
          <div class="row" style="gap:var(--space-3); flex-wrap:wrap;">
            <div class="field grow"><label>${esc(t('unit.meters.tier_quota') || 'Annual discounted quota')} ${opthint()}</label>
              <input class="input" name="tier_quota_annual" type="number" step="0.001" min="0" value="${esc(m.tier_quota_annual)}" placeholder="e.g. 2523" /></div>
            <div class="field grow"><label>${esc(t('unit.meters.tier_price_above') || 'Unit price above quota')} ${opthint()}</label>
              <input class="input" name="tier_price_above" type="number" step="0.0001" min="0" value="${esc(m.tier_price_above)}" placeholder="e.g. 70.1" /></div>
          </div>
          <div class="faint" style="font-size:12px;">${esc(t('unit.meters.tier_hint') || '')}</div>
        `}
        <div class="field"><label>${esc(t('unit.meters.reading_day') || 'Reading due day')} ${opthint()}</label>
          <input class="input" name="reading_day_of_month" type="number" step="1" min="1" max="31" value="${esc(m.reading_day_of_month)}" placeholder="e.g. 5" /></div>
        ${editing ? '' : `
          <div class="field"><label>${esc(t('unit.meters.starting_value') || 'Initial reading')} ${opthint()}</label>
            <input class="input" name="starting_value" type="number" step="0.001" min="0" value="${esc(m.starting_value)}" placeholder="${esc(t('unit.meters.starting_value_placeholder') || 'leave blank to set on first reading')}" /></div>
          <div class="field"><label>${esc(t('unit.meters.starting_date') || 'Reading date')}</label>
            <input class="input" name="starting_date" type="date" value="${esc(m.starting_date)}" /></div>
        `}
        <div class="field"><label>${esc(t('unit.meters.notes') || 'Notes')} ${opthint()}</label>
          <input class="input" name="notes" value="${esc(m.notes || '')}" placeholder="${esc(t('unit.meters.notes_placeholder') || 'e.g. handover value')}" /></div>
        ${editing ? '' : `
          <div class="field" style="display:${m.starting_value !== '' && m.starting_value != null ? 'block' : 'none'};">
            <label>${esc(t('unit.meters.starting_notes') || 'Initial-reading note')} ${opthint()}</label>
            <input class="input" name="starting_notes" value="${esc(m.starting_notes || '')}" placeholder="e.g. handover reading" />
          </div>
        `}
        <div class="row" style="justify-content:flex-end; gap:var(--space-3);">
          <button type="button" class="btn btn-ghost" data-action="cancel-add">${esc(t('common.cancel'))}</button>
          <button type="submit" class="btn btn-primary" ${this.state.busy ? 'disabled' : ''}>${esc(t(editing ? 'common.save' : 'unit.meters.save') || (editing ? 'Save' : 'Add meter'))}</button>
        </div>
      </form>
    `;
  }

  mount() {
    // Initial load: only kick off on the first render, not on every re-render
    // (each setState() re-runs mount()).
    if (this.state.meters === null && !this._loadStarted) {
      this._loadStarted = true;
      this._load();
    }
    const on = (sel, ev, fn) => {
      if (!this.el) return;
      this.el.querySelectorAll(sel).forEach((node) => node.addEventListener(ev, fn));
    };
    on('[data-action="open-add"]', 'click', () => {
      this.setState({ addOpen: true, panelOpen: true, newMeter: this._emptyMeter(), editingMeterId: null, editDraft: null });
    });
    on('[data-action="cancel-add"]', 'click', () => {
      this.setState({ addOpen: false, editingMeterId: null, editDraft: null });
    });
    on('[data-action="edit-meter"]', 'click', (e) => {
      const id = e.currentTarget.getAttribute('data-id');
      const row = (this.state.meters || []).find((m) => m.id === id);
      if (!row) return;
      this.setState({
        addOpen: true,
        panelOpen: true,
        editingMeterId: id,
        editDraft: this._draftFromRow(row),
      });
    });
    if (this.el && this.el.tagName === 'DETAILS') {
      // Sync user collapse/expand into state so subsequent re-renders preserve it.
      // The toggle event fires asynchronously, so by the time it runs `this.el`
      // may have been detached (re-render) — read from currentTarget instead.
      this.el.addEventListener('toggle', (e) => {
        const isOpen = !!(e.currentTarget && e.currentTarget.open);
        if (isOpen !== (this.state.panelOpen !== false)) {
          this.state.panelOpen = isOpen;
        }
      });
    }
    if (this.state.addOpen && this.el) {
      const form = this.el.querySelector('[data-add-form]');
      if (form) {
        const editing = this.state.editingMeterId != null;
        const target = editing ? this.state.editDraft : this.state.newMeter;
        form.querySelectorAll('input, select').forEach((inp) => {
          inp.addEventListener('input', (e) => {
            const name = e.target.name;
            if (!name) return;
            target[name] = e.target.value;
            if (name === 'kind' && !editing) {
              const next = DEFAULT_UOM[e.target.value] || '';
              const uomInput = form.querySelector('[name="unit_of_measure"]');
              if (uomInput && !uomInput.value) {
                uomInput.value = next;
                target.unit_of_measure = next;
              }
            }
          });
        });
        form.addEventListener('submit', (e) => this._submit(e));
      }
    }
    on('[data-action="remove-meter"]', 'click', (e) => {
      const id = e.currentTarget.getAttribute('data-id');
      this._remove(id);
    });
  }

  async _submit(e) {
    e.preventDefault();
    const editing = this.state.editingMeterId != null;
    const m = editing ? this.state.editDraft : this.state.newMeter;
    const payload = {
      kind: m.kind,
      label: (m.label || '').trim() || null,
      serial_number: (m.serial_number || '').trim() || null,
      unit_of_measure: (m.unit_of_measure || '').trim() || DEFAULT_UOM[m.kind] || '',
      notes: (m.notes || '').trim() || null,
    };
    const fee = parseFloat(String(m.monthly_fee || ''));
    payload.monthly_fee = Number.isFinite(fee) ? Math.round(fee * 100) : null;
    const price = parseFloat(String(m.unit_price || ''));
    payload.unit_price = Number.isFinite(price) ? Math.round(price * 100) : null;
    const quota = String(m.tier_quota_annual || '').trim();
    payload.tier_quota_annual = m.kind !== 'fee' && quota !== '' && isFinite(parseFloat(quota)) ? quota : null;
    const priceAbove = parseFloat(String(m.tier_price_above || ''));
    payload.tier_price_above = m.kind !== 'fee' && Number.isFinite(priceAbove) ? Math.round(priceAbove * 100) : null;
    const rdom = parseInt(String(m.reading_day_of_month || ''), 10);
    payload.reading_day_of_month = Number.isFinite(rdom) && rdom >= 1 && rdom <= 31 ? rdom : null;
    if (!editing) {
      const start = (m.starting_value || '').toString().trim();
      if (start !== '') payload.starting_value = parseFloat(start);
      const startDate = (m.starting_date || '').toString().trim();
      if (startDate !== '') payload.starting_date = startDate;
      const startNotes = (m.starting_notes || '').toString().trim();
      if (startNotes !== '') payload.starting_notes = startNotes;
    }
    this.setState({ busy: true });
    try {
      if (editing) {
        await api.put(`/meters/${this.state.editingMeterId}`, payload);
      } else {
        await api.post(`/units/${this.unitId}/meters`, payload);
      }
      this.setState({ busy: false, addOpen: false, editingMeterId: null, editDraft: null, newMeter: this._emptyMeter() });
      await this._load();
    } catch (err) {
      const msg = err instanceof ApiError ? (err.body && err.body.error) || err.message : 'Could not create meter';
      this.setState({ busy: false, error: msg });
    }
  }

  async _remove(id) {
    if (!id) return;
    if (!confirm(t('unit.meters.confirm_remove') || 'Remove this meter?')) return;
    try {
      await api.delete(`/meters/${id}`);
      await this._load();
    } catch (err) {
      const msg = err instanceof ApiError ? (err.body && err.body.error) || err.message : 'Could not delete meter';
      this.setState({ error: msg });
    }
  }
}

export function attachUnitMetersPanel(rootEl, props) {
  const esc = (typeof CSS !== 'undefined' && typeof CSS.escape === 'function')
    ? CSS.escape
    : (s) => String(s).replace(/["\\]/g, '\\$&');
  const slot = rootEl.querySelector(`[data-unit-meters-slot="${esc(props.unitId)}"]`);
  if (!slot) return null;
  const panel = new UnitMetersPanel(props);
  panel.render(slot);
  return panel;
}
